import React, { ReactNode } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DotLegalLink } from "@dotlegal/dotlegal-ui-components";
import { EntityType } from "@common/Common.types";
import { isPlainLeftClick } from "@common/isPlainLeftClick";
import { useUrlProvider } from "../../../useUrlProvider";

// The same size as every other pane's title, since the document pane shows this title in its header.
const titleFontSize = 14;

export interface DocumentPreviewTitleProps {
    entityType?: EntityType;
    entityId?: string;
    entityTitle?: string;
    fileName: string;
    /** "start" for a host as narrow as the rail, where centring spends the left half of the header on nothing. */
    align?: "center" | "start";
    onEntityLinkNavigate?: () => void;
}

export interface DocumentPreviewHeaderProps extends DocumentPreviewTitleProps {
    onClose?: () => void;
    /** Takes the close button's place at the right-hand end, for a host whose buttons are its own. */
    controls?: ReactNode;
}

export function useEntityUrl(entityType: EntityType | undefined, entityId: string | undefined): string | null {
    const urlProvider = useUrlProvider();
    if (entityType === undefined || !entityId) return null;
    switch (entityType) {
        case EntityType.Contract:
            return urlProvider.getContractOverviewUrl(entityId);
        case EntityType.System:
        case EntityType.SystemDocument:
            return urlProvider.getSystemOverviewUrl(entityId);
        case EntityType.ProcessingActivity:
        case EntityType.ProcessingActivityDocument:
            return urlProvider.getProcessingActivityOverviewUrl(entityId);
        case EntityType.IncidentLog:
        case EntityType.IncidentLogDocument:
            return urlProvider.getIncidentLogOverviewUrl(entityId);
        case EntityType.Process:
        case EntityType.ProcessDocument:
            return urlProvider.getProcessOverviewUrl(entityId);
        case EntityType.Policy:
        case EntityType.PolicyDocument:
            return urlProvider.getPolicyUrl(entityId);
        case EntityType.LegalEntity:
        case EntityType.LegalEntityDocument:
            return urlProvider.getLegalEntityUrl(entityId);
        case EntityType.AnnualWheelActivity:
            return urlProvider.getAnnualWheelUrlWithActivityId(entityId);
        default:
            return null;
    }
}

/** The entity the document hangs off, then the document itself. All a host needs to name what is on show. */
export function DocumentPreviewTitle({ entityType, entityId, entityTitle, fileName, align = "center", onEntityLinkNavigate }: DocumentPreviewTitleProps) {
    const entityUrl = useEntityUrl(entityType, entityId);

    // The link navigates on its own. A modified click opens the entity in a new tab and leaves this one as it
    // is, so only a plain click is the host's cue to put its viewer away.
    function handleEntityClick(event: React.MouseEvent) {
        if (isPlainLeftClick(event)) {
            onEntityLinkNavigate?.();
        }
    }

    return (
        <Box
            sx={(theme) => ({
                minWidth: 0,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: align === "center" ? "center" : "flex-start",
                gap: theme.spacing(0.75),
                overflow: "hidden",
                textTransform: "none",
            })}
        >
            {entityTitle && (
                <>
                    {entityUrl ? (
                        // The link inherits its size and weight, so they sit on the wrapper, which is also
                        // where the click is heard: DotLegalLink passes no onClick on to a routed link.
                        <Box onClick={handleEntityClick} sx={{ fontSize: titleFontSize, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
                            <DotLegalLink to={entityUrl} linkColor="primary">
                                {entityTitle}
                            </DotLegalLink>
                        </Box>
                    ) : (
                        <Typography
                            sx={(theme) => ({
                                fontSize: titleFontSize,
                                fontWeight: 600,
                                color: theme.palette.primary.dark,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            })}
                        >
                            {entityTitle}
                        </Typography>
                    )}
                    <Typography sx={(theme) => ({ fontSize: titleFontSize, color: theme.palette.text.secondary, flexShrink: 0 })}>·</Typography>
                </>
            )}
            <Typography
                sx={(theme) => ({
                    fontSize: titleFontSize,
                    color: theme.palette.text.secondary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                })}
            >
                {fileName}
            </Typography>
        </Box>
    );
}

/**
 * The header the fullscreen previewer and the chat's viewer draw for themselves. A document on the stage
 * gets the shell's header instead and uses DocumentPreviewTitle directly.
 */
function DocumentPreviewHeader({ onClose, controls, ...titleProps }: DocumentPreviewHeaderProps) {
    const isCentered = (titleProps.align ?? "center") === "center";

    return (
        <Box
            sx={(theme) => ({
                display: "grid",
                // An empty leading track mirrors the one the buttons sit in, so the title is centred against
                // the header itself while the buttons still get every pixel they need. Reserving a fixed
                // padding instead centres the title against the wrong box and lets it run under them.
                gridTemplateColumns: isCentered ? "1fr minmax(0, auto) 1fr" : "minmax(0, 1fr) auto",
                alignItems: "center",
                gap: theme.spacing(1),
                padding: theme.spacing(1.5, 2),
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                minHeight: 52,
            })}
        >
            {isCentered && <Box />}

            <DocumentPreviewTitle {...titleProps} />

            <Box sx={{ justifySelf: "end", display: "flex", alignItems: "center" }}>
                {controls ??
                    (onClose && (
                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    ))}
            </Box>
        </Box>
    );
}

export default DocumentPreviewHeader;
